import { mapSize } from '../constants/constants';

/**
 * Shared avatar layout math (TT-1166).
 *
 * Coach renders the Spine rig and is the sole authority on where it is drawn. The TT-1166
 * "thinking" bubble needs to anchor to that rig's on-screen head, so rather than
 * re-deriving the pose (which drifts from Coach, especially on resize — Coach freezes the
 * world pose at render/OSK-toggle time and has no resize listener), Coach PUBLISHES the
 * world pose it actually applied via setAvatarPose(), and the bubble projects that exact
 * pose through the measured canvas rect. Single source of truth, nothing to keep in sync.
 *
 * The rig is drawn into a fixed WebGL buffer (constants.mapSize, 2048x1152) that is
 * stretched to fill the on-screen canvas, so a world coordinate maps linearly to the same
 * fraction of the canvas's bounding rect. World origin is bottom-left (Spine is Y-up).
 */

export const AVATAR_WORLD = Object.freeze({ width: mapSize.width, height: mapSize.height });

// World-space rig positions (mirrors the POSITIONS map Coach uses to place the rig).
// NOTE: NEW_RIG_ON_SCREEN_KEYBOARD is intentionally unused — the Amira2 rig deliberately
// does NOT move when the on-screen keyboard opens (see Coach.adjustAmiraTo*). Kept to
// mirror Coach's constant set.
export const AVATAR_POSITIONS = Object.freeze({
  DEFAULT: { X: 1650, Y: 340 },
  ON_SCREEN_KEYBOARD: { X: 1650, Y: 0 },
  SMALL_SCREEN: { X: 1550, Y: 340 },
  NEW_RIG_DEFAULT: { X: 1550, Y: 350 },
  NEW_RIG_ON_SCREEN_KEYBOARD: { X: 1500, Y: 0 },
  NEW_RIG_SMALL_SCREEN: { X: 1500, Y: 350 },
});

export const AVATAR_SCALE = Object.freeze({
  DEFAULT: 0.70,
  NEW_RIG: 0.55,
});

export function isAmira2Rig(rig) {
  return rig === 'Amira2';
}

// --- Published avatar pose ------------------------------------------------------------
// Coach writes the world pose it last applied here; the bubble reads it. Module-level so
// it is shared without threading it through React. A window event lets the bubble
// re-measure when the pose changes (OSK toggle, rig load) without polling.
export const AVATAR_POSE_EVENT = 'amiraAvatarPoseApplied';

let appliedPose = null;

/**
 * Record the world pose Coach just applied to the rig, and notify listeners.
 * @param {number} x
 * @param {number} y
 */
export function setAvatarPose(x, y) {
  appliedPose = { x, y };
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AVATAR_POSE_EVENT));
  }
}

/** @returns {{ x: number, y: number } | null} The last pose Coach applied, if any. */
export function getAvatarPose() {
  return appliedPose;
}

// Head anchor offset from the rig root position (pos), in world units, added directly by
// projectHeadToScreen. These are per-rig and SCALE-INCLUSIVE — the rig's own scale is
// already baked in, so they are NOT independent skeleton-space offsets and will need
// re-tuning if a rig's SCALE changes. They are the only bubble-specific tunables and are
// viewport-independent — the intended QA tuning point.
export const AVATAR_HEAD_OFFSET = Object.freeze({
  Amira: { dx: 60, dy: 610 },
  Amira2: { dx: 110, dy: 520 },
});

export function getHeadOffset(rig) {
  return isAmira2Rig(rig) ? AVATAR_HEAD_OFFSET.Amira2 : AVATAR_HEAD_OFFSET.Amira;
}

/**
 * Project the rig's head world position into viewport pixels, given the canvas's on-screen
 * bounding rect and the rig's applied world pose. Screen Y is flipped because world origin
 * is bottom-left.
 * @returns {{ left: number, top: number }}
 */
export function projectHeadToScreen(rect, rig, pose) {
  const { dx, dy } = getHeadOffset(rig);
  const worldX = pose.x + dx;
  const worldY = pose.y + dy;
  return {
    left: rect.left + (worldX / AVATAR_WORLD.width) * rect.width,
    top: rect.top + ((AVATAR_WORLD.height - worldY) / AVATAR_WORLD.height) * rect.height,
  };
}

// The static WebGL-error fallback is a PNG with no world coordinates, so anchor to a
// fraction of the fallback image's bounding rect instead (tunable). The static Amira
// pose sits lower/left of the live rig, so the head anchor is shifted right and up
// relative to the projected-pose path to keep the bubble off her face.
export const STATIC_FALLBACK_HEAD_FRACTION = Object.freeze({ x: 0.7, y: 0.04 });
